import { ReviewRoute, ReviewStop } from './confirm-review';
import { decodePolylineNear, PolylinePoint } from './encoded-polyline';
import {
  LatLon,
  PlanOrder,
  PlanRoute,
  Reload,
  RouteMapRequest,
  SortRequest,
  SortResponse,
  SortStop,
  Truck,
  Unplanned,
} from './route-optimization.interface';
import { addMinutesToClock, clockToMinutes } from './route-times';

/**
 * El recorrido REAL de una ruta editada, pedido al motor.
 *
 * Existe porque el gestor no tenía con qué medir lo que edita, y su encabezado lo
 * declaraba como un límite del motor: *"no existe endpoint para reevaluar un orden
 * fijo"*. Ese endpoint sí existe (`POST /sort`) y lo usa el drawer de sub-rutas
 * desde antes de que el gestor existiera.
 *
 * **`/sort` REORDENA.** No mide una secuencia dada: recibe paradas y devuelve el
 * mejor recorrido para ELLAS. Recalcular no es "cronometrá lo mío" sino "resolvé
 * esta ruta con lo que ahora tiene", y por eso el control lo dice antes de
 * accionarse. La alternativa que respeta una secuencia fija —`/evaluate-route`—
 * existe, pero **no devuelve `geometry`** y el trayecto en el mapa es requisito.
 *
 * Módulo puro: entran una ruta y supuestos, sale el cuerpo de la petición; entra la
 * respuesta, salen las paradas reordenadas y sus horas. Sin HTTP, sin Angular.
 */

/** Los supuestos CONGELADOS de la corrida contra los que se recalcula. */
export interface RecalcAssumptions {
  /** Origen del recorrido. El camión sale de acá y vuelve. */
  warehouse: LatLon;
  /** Hora de salida (`HH:MM`): la base de todas las llegadas. */
  shiftStart: string;
  /** Fin del turno (`HH:MM`), para poder declarar el exceso. */
  shiftEnd: string;
  /** Minutos de atención por parada, los de la corrida y no los del selector. */
  serviceMinPerStop: number;
}

/** Los tiempos de UNA parada dentro del recorrido recalculado. */
export interface RecalcStopTimes {
  /** Minutos desde la salida hasta llegar acá (incluye la atención acumulada). */
  etaMin: number;
  arrivalTime: string;
  departureTime: string;
  /** Viaje desde la parada anterior; en la primera, desde el almacén. */
  legDurationMin: number;
  legDistanceKm: number;
  /**
   * Viaje al que pertenece la parada.
   *
   * `1` siempre con `sort`, que no conoce viajes. Con `plan` es el del motor, y es
   * lo que permite separar los viajes con su recarga sin preguntar por el origen.
   */
  trip: number;
}

/**
 * Con cuál de las dos preguntas se obtuvo el resultado.
 *
 * No es cosmético: decide qué avisos son ciertos. `'sort'` no modela recargas, así
 * que en una ruta multi-viaje su tiempo es un PISO; `'plan'` sí las modela y el
 * mismo aviso ahí sería falso.
 */
export type RecalcSource = 'sort' | 'plan';

/** Una parada que el motor no pudo ubicar al replanificar. */
export interface UnplacedStop {
  orderId: string;
  /** Motivo del motor (`exceeds_capacity`, etc.). Se muestra tal cual. */
  reason: string;
}

/** El resultado de un recálculo, y la secuencia que describe. */
export interface RouteRecalc {
  /** Con cuál acción se obtuvo. Ver `RecalcSource`. */
  source: RecalcSource;
  /**
   * Paradas que el motor devolvió sin ubicar. Siempre vacío con `'sort'`, que no
   * puede descartar ninguna.
   */
  unplaced: UnplacedStop[];
  /**
   * La secuencia EXACTA que estos números describen.
   *
   * Es lo que hace derivable la obsolescencia (`recalcIsStale`) en vez de exigir un
   * flag que alguien tenga que acordarse de apagar en las siete mutaciones que
   * cambian una ruta —y que se apagaría en seis.
   */
  sequence: string[];
  stops: Map<string, RecalcStopTimes>;
  totalDurationMin: number;
  totalDistanceKm: number;
  /** Hora de salida de la última parada: el fin de la jornada que se puede afirmar. */
  endTime: string;
  /** El trayecto ya decodificado, o vacío si no se pudo. */
  path: PolylinePoint[];
  /**
   * `true` si la ruta lleva más de un viaje. `/sort` no modela el regreso al almacén
   * a recargar, así que en ese caso el número es un **piso**, no el tiempo real.
   */
  multiTrip: boolean;
  /** Paradas que no se pudieron mandar por no tener coordenada resuelta. */
  skippedStops: number;
  /**
   * Regresos al almacén entre viajes.
   *
   * Vacío con `sort`, que no los modela. Que llegue vacío es lo que hace que el
   * divisor de recarga desaparezca solo en la pantalla: pintarlo ahí afirmaría un
   * regreso al almacén que ese cálculo no consideró.
   */
  reloads: Reload[];
}

/** `[lat, lon]` de una parada, resuelto por el llamador. */
export type StopCoordOf = (orderId: string) => [number, number] | undefined;

/** Las paradas de la ruta que se pueden medir, en su orden vigente. */
function measurableStops(
  route: ReviewRoute,
  coordOf: StopCoordOf,
): Array<{ stop: ReviewStop; coord: [number, number] }> {
  const out: Array<{ stop: ReviewStop; coord: [number, number] }> = [];
  for (const stop of route?.stops ?? []) {
    const coord = coordOf(stop.orderId);
    if (coord) out.push({ stop, coord });
  }
  return out;
}

/**
 * ¿Esta ruta se puede recalcular? El contrato de `/sort` pide **mínimo dos**
 * paradas, así que con menos se declara en vez de mandar una petición que vuelve
 * `422`.
 */
export function canRecalcRoute(route: ReviewRoute, coordOf: StopCoordOf): boolean {
  return measurableStops(route, coordOf).length >= 2;
}

/**
 * El cuerpo de `POST /sort` para una ruta.
 *
 * `round_trip: true` porque el camión vuelve al almacén — el mismo supuesto con el
 * que ya se pide el baseline del KPI de ahorro. La atención sale de los supuestos
 * congelados de la corrida y no del selector, que el operador pudo mover después de
 * correr.
 */
export function toSortRequestForRoute(
  route: ReviewRoute,
  coordOf: StopCoordOf,
  assumptions: RecalcAssumptions,
): SortRequest {
  const stops: SortStop[] = measurableStops(route, coordOf).map(({ stop, coord }) => ({
    id: stop.orderId,
    lat: coord[0],
    lon: coord[1],
    service_min: assumptions.serviceMinPerStop,
  }));
  return { stops, start: assumptions.warehouse, round_trip: true };
}

/**
 * Aplica la respuesta del motor: **reordena** la ruta y arma sus tiempos.
 *
 * Las paradas que la respuesta no nombra se conservan al final. No debería haberlas
 * —se manda lo que la ruta tiene— pero descartarlas sacaría del día una entrega que
 * nadie declaró como no entregada, que es la misma red de seguridad que ya tiene el
 * reparto del delta.
 */
export function applySortResult(
  route: ReviewRoute,
  response: SortResponse,
  assumptions: RecalcAssumptions,
): RouteRecalc {
  const byId = new Map((response?.order ?? []).map((o) => [o.id, o]));
  const placed = new Set<string>();
  const ordered: ReviewStop[] = [];

  for (const item of [...(response?.order ?? [])].sort((a, b) => a.sequence - b.sequence)) {
    const stop = route.stops.find((s) => s.orderId === item.id);
    if (!stop || placed.has(item.id)) continue;
    ordered.push(stop);
    placed.add(item.id);
  }
  const kept = route.stops.filter((s) => !placed.has(s.orderId));
  route.stops = [...ordered, ...kept];

  const stops = new Map<string, RecalcStopTimes>();
  let endTime = '';
  for (const stop of ordered) {
    const item = byId.get(stop.orderId);
    if (!item) continue;
    const arrivalTime = addMinutesToClock(assumptions.shiftStart, item.eta_min);
    const departureTime = addMinutesToClock(arrivalTime, assumptions.serviceMinPerStop);
    stops.set(stop.orderId, {
      etaMin: item.eta_min,
      arrivalTime,
      departureTime,
      legDurationMin: item.leg_duration_min,
      legDistanceKm: item.leg_distance_km,
      // `/sort` no conoce viajes: todo es el primero.
      trip: 1,
    });
    endTime = departureTime || endTime;
  }

  return {
    source: 'sort',
    unplaced: [],
    sequence: route.stops.map((s) => s.orderId),
    stops,
    totalDurationMin: response?.total_duration_min ?? 0,
    totalDistanceKm: response?.total_distance_km ?? 0,
    endTime,
    // La precisión de la polyline no está en el contrato: se decide contra el
    // almacén, que es un punto que ya conocemos. Ver `encoded-polyline`.
    path: decodePolylineNear(response?.geometry ?? '', assumptions.warehouse),
    multiTrip: (route.trips ?? 1) > 1,
    skippedStops: kept.length,
    reloads: [],
  };
}

/* ---------- La otra pregunta: replanificar con `/route-map` ---------- *
 *
 * `Reordenar` (arriba) pregunta *"¿cuál es el mejor recorrido para TODO esto?"* y
 * nunca saca nada. `Replanificar` pregunta *"¿qué cabe en ESTE camión, en ESTE
 * turno?"* y puede devolver paradas.
 *
 * No hace falta nada nuevo del motor: `/route-map` **re-planifica** (su contrato lo
 * dice) y con un solo camión resuelve una ruta sola. A cambio de poder devolver
 * pedidos, trae `arrival_time` DIRECTO, modela las recargas y entrega el trayecto ya
 * decodificado en el servidor.
 */

/** El camión de la ruta, tal como lo declara el maestro y lo usó la corrida. */
export interface RecalcTruck {
  id: string;
  capacityLb: number;
  /** Viajes del turno (1 = sin recargas). */
  trips: number;
  /** Minutos de recarga entre viajes. */
  reloadMin: number;
}

/** `shipTo._id` de una parada; solo se usa si el llamador quiere declararlo. */
export type CustomerOf = (orderId: string) => string | undefined;

/**
 * El cuerpo de `POST /route-map` para replanificar UNA ruta.
 *
 * **Los pedidos se arman desde las PARADAS, no desde las órdenes.** Las paradas ya
 * son las unidades post-agrupación de la corrida —`groupOrdersByCustomer` produce los
 * `__sN`— con su peso propio. Reconstruir desde las órdenes volvería a agrupar y a
 * intentar partir, y con `splittable: false` una visita dividida saldría entera como
 * `exceeds_capacity`: el replan rechazaría justo lo que la corrida sí repartió.
 *
 * Por lo mismo `customer_id` es **el id de la parada**: declarar el cliente real
 * invitaría al motor a re-agrupar unidades que esta pantalla ya agrupó, cambiando el
 * conjunto de paradas bajo el operador.
 */
export function toPlanRequestForRoute(
  route: ReviewRoute,
  coordOf: StopCoordOf,
  assumptions: RecalcAssumptions,
  truck: RecalcTruck,
  date: string,
): RouteMapRequest {
  const orders: PlanOrder[] = measurableStops(route, coordOf).map(({ stop, coord }) => ({
    id: stop.orderId,
    customer_id: stop.orderId,
    lat: coord[0],
    lon: coord[1],
    weight: stop.weightLb ?? 0,
    service_min: assumptions.serviceMinPerStop,
    // La misma regla de negocio de la corrida: NO hay entrega parcial.
    splittable: false,
  }));

  const shift = { start: assumptions.shiftStart, end: assumptions.shiftEnd };
  const one: Truck = {
    id: truck.id,
    capacity: truck.capacityLb,
    shift,
    ...(truck.trips > 1 ? { max_trips: truck.trips, reload_min: truck.reloadMin } : {}),
  };

  return {
    date,
    warehouse: assumptions.warehouse,
    orders,
    weight_unit: 'lb',
    trucks: [one],
  };
}

/**
 * Aplica el plan de una sola ruta: la reordena y toma sus horas **directas**.
 *
 * Las horas no se derivan: el motor escribe `arrival_time` y `departure_time`, y
 * `departure − arrival` es la atención que realmente asignó. Derivarlas acá sería
 * recalcular algo que ya vino resuelto y podría discrepar.
 *
 * Lo que vuelve sin ubicar **se queda al final de la ruta, sin tiempos**. Descartarlo
 * sacaría del día una entrega que nadie declaró como no entregada, y devolverlo a los
 * pendientes solo funcionaría para las paradas que vinieron de ahí: las del plan
 * desaparecerían de las dos listas.
 */
export function applyPlanResult(
  route: ReviewRoute,
  planRoute: PlanRoute | undefined,
  unassigned: readonly Unplanned[] | undefined,
  path: PolylinePoint[],
  assumptions: RecalcAssumptions,
): RouteRecalc {
  const planned = [...(planRoute?.stops ?? [])].sort((a, b) => a.sequence - b.sequence);
  const placed = new Set<string>();
  const ordered: ReviewStop[] = [];
  for (const item of planned) {
    const stop = route.stops.find((s) => s.orderId === item.order_id);
    if (!stop || placed.has(item.order_id)) continue;
    ordered.push(stop);
    placed.add(item.order_id);
  }
  const kept = route.stops.filter((s) => !placed.has(s.orderId));
  route.stops = [...ordered, ...kept];

  const reasonById = new Map((unassigned ?? []).map((u) => [u.order_id, u.reason]));
  const stops = new Map<string, RecalcStopTimes>();
  let endTime = '';
  let totalDistanceKm = 0;
  for (const item of planned) {
    if (!placed.has(item.order_id)) continue;
    stops.set(item.order_id, {
      etaMin: item.eta_min ?? 0,
      arrivalTime: item.arrival_time,
      departureTime: item.departure_time,
      legDurationMin: item.leg_duration_min ?? 0,
      legDistanceKm: item.leg_distance_km ?? 0,
      trip: item.trip ?? 1,
    });
    totalDistanceKm += item.leg_distance_km ?? 0;
    endTime = item.departure_time || endTime;
  }

  return {
    source: 'plan',
    // Cada parada que quedó fuera, con el motivo del motor. Las que el motor no
    // nombró llevan cadena vacía: no se inventa una causa.
    unplaced: kept.map((s) => ({ orderId: s.orderId, reason: reasonById.get(s.orderId) ?? '' })),
    sequence: route.stops.map((s) => s.orderId),
    stops,
    totalDurationMin: planRoute?.total_duration_min ?? 0,
    // `PlanRoute` no informa distancia total: se suma de los tramos, que sí vienen.
    totalDistanceKm: Math.round(totalDistanceKm * 10) / 10,
    endTime,
    path,
    multiTrip: (route.trips ?? 1) > 1,
    skippedStops: kept.length,
    // El plan SÍ modela los viajes: sus recargas viajan y el divisor se puede pintar.
    reloads: planRoute?.reloads ?? [],
  };
}

/**
 * ¿El resultado dejó de describir la ruta?
 *
 * Se DERIVA de la secuencia y no de un flag. Un flag habría que apagarlo en las
 * siete operaciones que cambian una ruta —reordenar, cruzar, incorporar, quitar,
 * restaurar, adoptar el orden guardado, repartir el delta— y bastaría olvidarse en
 * una para mostrar horas de una secuencia que ya no existe. Es el mismo criterio con
 * el que `custom` se recalcula contra el original en vez de encenderse y quedarse.
 */
export function recalcIsStale(route: ReviewRoute, recalc: RouteRecalc | undefined): boolean {
  if (!recalc) return true;
  const current = route?.stops ?? [];
  if (current.length !== recalc.sequence.length) return true;
  return current.some((s, i) => s.orderId !== recalc.sequence[i]);
}

/**
 * Minutos por encima del fin del turno, o `0` si cabe.
 *
 * Se DECLARA, no bloquea: el plan ordenó otro conjunto de paradas y el operador ya
 * decidió cambiarlo. Es la misma postura que el gestor tiene con la sobrecarga de
 * carga —insignia roja con el exceso, sin impedir confirmar—.
 */
export function recalcExceedsShiftMin(
  recalc: RouteRecalc | undefined,
  shiftEnd: string,
): number {
  const end = clockToMinutes(recalc?.endTime);
  const limit = clockToMinutes(shiftEnd);
  if (end === null || limit === null) return 0;
  return Math.max(0, end - limit);
}
